import {useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PageHeading} from '@/components/page-heading'
import {sgsstPageGuard} from '@/components/no-access-state'
import {PlanLockedPage} from '@/components/plan-upsell'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {IpevrExportButtons} from '@/components/ipevr/export-buttons'
import {MatrizRiesgosGrid} from '@/components/matriz-riesgos-grid'
import {DescartesTab} from '@/components/ipevr/descartes-tab'
import {CertificacionTab} from '@/components/certificacion-tab'
import {MatrizColumnaCargos} from '@/components/matriz-columna-cargos'
import {useBusiness, useCapabilities, useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useMatrizRiesgos} from '@/hooks/sgsst/riesgos/use-ipevr'

type TabKey = 'matriz' | 'cargos' | 'descartados' | 'certificacion'

const TABS: TabKey[] = ['matriz', 'cargos', 'descartados', 'certificacion']

function parseTab(value: string | null): TabKey | null {
  return TABS.find((tab) => tab === value) ?? null
}

/** «Cargos» es la columna derivada de E1-H02: pestaña propia porque no se escribe. */
export function MatrizRiesgosPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {data: business, isLoading: businessLoading} = useBusiness(businessId)
  const capabilities = useCapabilities(businessId)
  const canOperate = capabilities.sgsst_operar
  const canAccess = canOperate
  const {data: matriz, isLoading} = useMatrizRiesgos(canAccess ? businessId : undefined)
  const isSystemUser = useIsSystemUser()
  const godReadOnly = useGodModeReadOnly()
  const canEdit = canOperate && !godReadOnly
  // Ignorar un aviso de «cargos» es firmar (`require_firmante`, que nunca admite modo dios virtual), no
  // operar: con `canEdit` el modo dios vería el botón y recibiría un 403.
  const puedeFirmar = capabilities.sgsst_firmar && !godReadOnly
  const razonBloqueoFirma = godReadOnly
    ? t(capabilities.sgsst_firmar ? 'matrizCargos.bloqueoSeguro' : 'matrizCargos.bloqueoModoDios')
    : capabilities.firmar_bloqueado_por
  const [searchParams, setSearchParams] = useSearchParams()

  const guard = sgsstPageGuard(businessLoading || isLoading, canAccess, matriz)
  if (guard) return guard
  if (!matriz || !businessId) return null

  if (!capabilities.can_use_matriz) {
    return <PlanLockedPage feature={t('plans.feature.matriz')} businessId={businessId} businessName={business?.name} />
  }

  const tab = parseTab(searchParams.get('tab')) ?? 'matriz'
  function setTab(next: TabKey) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      {replace: true}
    )
  }

  return (
    <div className="space-y-5">
      {/* Sin `accion`: el trabajo de la matriz ocurre dentro de la pestaña y exportar ahí se leería como el
          primario de la vista (U7; precedente `rejilla-cargo.tsx`). */}
      <PageHeading title={t('ipevr.title')} />
      {businessId && (
        <div className="flex flex-wrap items-center justify-end gap-2" data-testid="ipevr-export-slot">
          <IpevrExportButtons businessId={businessId} />
        </div>
      )}

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="matriz" data-testid="tab-matriz">
            {t('ipevr.tabs.matriz')}
          </TabsTrigger>
          <TabsTrigger value="cargos" data-testid="tab-cargos">
            {t('matrizCargos.tab')}
          </TabsTrigger>
          <TabsTrigger value="descartados" data-testid="tab-descartados">
            {t('ipevr.tabs.descartados')}
          </TabsTrigger>
          <TabsTrigger value="certificacion" data-testid="tab-certificacion">
            {t('ipevr.tabs.certificacion')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="matriz" className="pt-4">
          <MatrizRiesgosGrid
            businessId={businessId}
            matriz={matriz}
            canOperate={canOperate}
            canEdit={canEdit}
            isSystemUser={isSystemUser}
          />
        </TabsContent>

        <TabsContent value="cargos" className="pt-4">
          <MatrizColumnaCargos
            businessId={businessId}
            active={tab === 'cargos'}
            puedeIgnorar={puedeFirmar}
            bloqueoFirma={razonBloqueoFirma}
          />
        </TabsContent>

        <TabsContent value="descartados" className="pt-4">
          <DescartesTab businessId={businessId} active={tab === 'descartados'} />
        </TabsContent>

        <TabsContent value="certificacion" className="pt-4">
          <CertificacionTab businessId={businessId} artefacto="matriz_riesgos" />
        </TabsContent>
      </Tabs>
    </div>
  )
}
